import { useState } from "react";
import DashboardLayout from "../layout";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  createCoupon,
  getCoupons,
  editCoupon,
  Coupon,
  CreateCouponPayload,
  EditCouponPayload,
} from "services/coupon";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "components/Toast";

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const Promo = () => {
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState("");
  const [newCoupon, setNewCoupon] = useState({
    name: "",
    expire: new Date(), // Store as Date object for DatePicker
    type: "percentage" as "percentage" | "fixed",
    percentage: "",
    amount: "",
  });

  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["coupons"],
    queryFn: getCoupons,
  });

  console.log("Fetched coupons:", data?.data);

  const createCouponMutation = useMutation({
    mutationFn: createCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
      showToast("Coupon created successfully!", "success");
      setOpen(false);
      setNewCoupon({
        name: "",
        expire: new Date(),
        type: "percentage",
        percentage: "",
        amount: "",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create coupon. Please try again.";
      showToast(errorMessage, "error");
      console.error("Failed to create coupon:", error);
    },
  });

  const editCouponMutation = useMutation({
    mutationFn: editCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["coupons"] });
      showToast("Coupon updated successfully!", "success");
      setOpen(false);
      setEditMode(false);
      setSelectedCouponId("");
      setNewCoupon({
        name: "",
        expire: new Date(),
        type: "percentage",
        percentage: "",
        amount: "",
      });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update coupon. Please try again.";
      showToast(errorMessage, "error");
      console.error("Failed to update coupon:", error);
    },
  });

  const handleCreateCoupon = () => {
    // Create the payload with the correct type
    const payload: CreateCouponPayload = {
      name: newCoupon.name,
      expire: newCoupon.expire.toLocaleDateString("en-US"),
      type: newCoupon.type,
    };

    // Add either percentage or amount based on the selected type
    if (newCoupon.type === "percentage") {
      payload.percentage = Number(newCoupon.percentage) || 0;
    } else {
      payload.amount = Number(newCoupon.amount) || 0;
    }

    createCouponMutation.mutate(payload);
  };

  const handleEditCoupon = () => {
    // Create the payload with the correct type
    const payload: EditCouponPayload = {
      _id: selectedCouponId,
      name: newCoupon.name,
      expire: newCoupon.expire.toLocaleDateString("en-US"),
      type: newCoupon.type,
    };

    // Add either percentage or amount based on the selected type
    if (newCoupon.type === "percentage") {
      payload.percentage = Number(newCoupon.percentage) || 0;
    } else {
      payload.amount = Number(newCoupon.amount) || 0;
    }

    editCouponMutation.mutate(payload);
  };

  const handleOpenEditDialog = (coupon: Coupon) => {
    setEditMode(true);
    setSelectedCouponId(coupon._id);
    setNewCoupon({
      name: coupon.name,
      expire: new Date(coupon.expire),
      type: coupon.type,
      percentage: coupon.percentage ? String(coupon.percentage) : "",
      amount: coupon.amount ? String(coupon.amount) : "",
    });
    setOpen(true);
  };

  // Add copy to clipboard functionality
  const handleCopyToClipboard = (text: string, type: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        showToast(`${type} copied to clipboard!`, "success");
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        showToast("Failed to copy to clipboard", "error");
      });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DashboardLayout nextSideBar>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5">Coupon Management</Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpen(true)}
          >
            Create New Coupon
          </Button>
        </Box>

        <TableContainer
          sx={{
            border: "1px solid #E4E7EC",
            borderRadius: "6px",
            boxShadow: "none",
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Coupon Code</TableCell>
                <TableCell>Expiry Date</TableCell>
                <TableCell>Discount Type</TableCell>
                <TableCell>Discount Value</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.data?.coupons?.map((coupon: Coupon) => (
                <TableRow key={coupon._id}>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="Copy name">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyToClipboard(coupon.name, "Name")
                          }
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                      {coupon.name}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="Copy code">
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleCopyToClipboard(coupon.code, "Code")
                          }
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </Tooltip>
                      {coupon.code}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(coupon.expire)}</TableCell>
                  <TableCell>
                    {coupon.type === "fixed" ? "Fixed Amount" : "Percentage"}
                  </TableCell>
                  <TableCell>
                    {coupon.type === "fixed"
                      ? `₦${coupon?.amount?.toLocaleString()}`
                      : coupon.percentage
                      ? `${coupon?.percentage}%`
                      : "------"}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleOpenEditDialog(coupon)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={open}
          onClose={() => setOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              minWidth: "350px",
            },
          }}
        >
          <DialogTitle sx={{ fontSize: "18px", pb: 2 }}>
            {editMode ? "Edit Coupon" : "Create New Coupon"}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
            >
              <TextField
                label="Coupon Name"
                value={newCoupon.name}
                onChange={(e) =>
                  setNewCoupon({ ...newCoupon, name: e.target.value })
                }
                fullWidth
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Expiry Date"
                  value={newCoupon.expire}
                  onChange={(date) =>
                    setNewCoupon({ ...newCoupon, expire: date || new Date() })
                  }
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: "outlined" as const,
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                            color: "#444",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Discount Type
                </Typography>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant={
                      newCoupon.type === "percentage" ? "contained" : "outlined"
                    }
                    onClick={() =>
                      setNewCoupon({ ...newCoupon, type: "percentage" })
                    }
                  >
                    Percentage
                  </Button>
                  <Button
                    variant={
                      newCoupon.type === "fixed" ? "contained" : "outlined"
                    }
                    onClick={() =>
                      setNewCoupon({ ...newCoupon, type: "fixed" })
                    }
                  >
                    Fixed Amount
                  </Button>
                </Box>
              </Box>

              {newCoupon.type === "percentage" ? (
                <TextField
                  label="Discount Percentage"
                  type="number"
                  value={newCoupon.percentage}
                  onChange={(e) =>
                    setNewCoupon({
                      ...newCoupon,
                      percentage: e.target.value,
                    })
                  }
                  fullWidth
                  InputProps={{
                    endAdornment: <Typography variant="body2">%</Typography>,
                  }}
                />
              ) : (
                <TextField
                  label="Fixed Amount"
                  type="number"
                  value={newCoupon.amount}
                  onChange={(e) =>
                    setNewCoupon({
                      ...newCoupon,
                      amount: e.target.value,
                    })
                  }
                  fullWidth
                  InputProps={{
                    startAdornment: <Typography variant="body2">₦</Typography>,
                  }}
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
            <Button
              onClick={() => {
                setOpen(false);
                if (editMode) {
                  setEditMode(false);
                  setSelectedCouponId("");
                  setNewCoupon({
                    name: "",
                    expire: new Date(),
                    type: "percentage",
                    percentage: "",
                    amount: "",
                  });
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={editMode ? handleEditCoupon : handleCreateCoupon}
              variant="contained"
              disabled={
                editMode
                  ? editCouponMutation.isPending
                  : createCouponMutation.isPending
              }
            >
              {editMode
                ? editCouponMutation.isPending
                  ? "Updating..."
                  : "Update"
                : createCouponMutation.isPending
                ? "Creating..."
                : "Create"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
};

export default Promo;
